import Game from '../../Game';
import TextComponent from '../../Component/Render/Text';
import Renderable from '../../Component/Renderable';
import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import SpriteComponent from '../../Component/Render/Sprite';
import { ArrayHelper } from '../../Utility/ArrayHelper';
import { Random } from '../../Utility/Random';
import AnimationComponent from '../../Component/Animation';
import { StartAnimation } from '../Animation/AnimationHelper';
import { GameActions } from '../../State/Game/GameActions';
import UIImageComponent from '../../Component/UI/Image';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import { IFriendData } from '../../Player/PlayerProperty/Social/IPlayerSocialData';
import DateHelper from '../../Utility/DateHelper';
import { HELPER_FRIENDS_COOLDOWN } from '../../Constants/Timers';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { ContextType } from '../../State/Game/ContextType';
import { IScoreEntry } from '../../Utility/SDK/Drivers/IScoreEntry';
import { FBContextHandler, FBContextTypes } from '../../Utility/SDK/Drivers/FBContextHandler';

/**
 * Enum mapping to legacy leaderboard data.
 */
export enum Leaderboard {
    /**
     * Score earned on each level. InstanceId = Level Number (as string)
     */
    LEVEL_SCORE = 'default',

    /**
     * Number of stars earned on each world. InstanceId = World Number (as string)
     */
    WORLD_STARS = 'stars',

    /**
     * Total number of stars earned. InstanceId = 'default'
     */
    TOTAL_STARS = 'stars',

    /**
     * The number of levels that have been completed by the user. InstanceId = 'default'
     */
    TOTAL_LEVELS = 'completedLevels',
}

export const TOTAL_STARS_INSTANCE_ID = 'default';

// Total Stars leaderboard back colors:
const PLAYER_BG_COLOR = 0xfcfbbf;
const FRIEND_BG_COLOR = 0xffe8d5;

//
const ANIMAL_ENTRY_PLACEHOLDERS: { name: string; avatarId: string }[] = [
    {
        name: 'Sparky',
        avatarId: 'avatar1',
    },
    {
        name: 'Kiwi',
        avatarId: 'avatar2',
    },
    {
        name: 'Foxy',
        avatarId: 'avatar3',
    },
    {
        name: 'Sushi',
        avatarId: 'avatar4',
    },
    {
        name: 'Cutey',
        avatarId: 'avatar5',
    },
    {
        name: 'Rocky',
        avatarId: 'avatar6',
    },
];

//
interface IWorldLeaderBoardEntry {
    name: string;
    score: number;
    avatarId: string;
}

/**
 * Cache used to store randomly selected animal avatar with random score as a placeholder
 * in the world leaderboard (travel menu).
 */
const WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE: {
    [key: string]: [
        IWorldLeaderBoardEntry,
        IWorldLeaderBoardEntry,
        IWorldLeaderBoardEntry
    ];
} = {};
//

export default class LeaderboardHelper {
    /**
     * Look-up dictionaries to find items in lists by the player ID.
     * TODO: Make this better...
     */
    public static livesMap: { [playerId: string]: number };
    public static friendsMap: { [playerId: string]: number };

    public static async loadLevelMenuLeaderboard(
        topList: GameObject,
        levelNumber: number
    ) {
        // Hide placeholder leader-board entries
        for (let i = 1; i <= 4; i++) {
            topList
                .find(`leaders/player${i}`)
                .getComponent(Renderable).visible = false;
        }

        // Load friends scores and limit to four entries.
        const leaderboardEntries: IScoreEntry[] = (
            await Game.sdk.getFriendScores(
                Leaderboard.LEVEL_SCORE,
                levelNumber.toString()
            )
        ).slice(0, 4);

        // Load all avatars.
        await LeaderboardHelper.getPlayerAvatars(leaderboardEntries);

        leaderboardEntries.forEach((entry, index) => {
            // Show entry.
            topList
                .find(`leaders/player${index + 1}`)
                .getComponent(Renderable).visible = true;

            // Set entry name.
            const textName = topList.find(`leaders/player${index + 1}/name`);
            textName.getComponent(TextComponent).text = entry.user.handle.slice(
                0,
                8
            ); // Limit to 8 characters.

            // Set entry score.
            const textScore = topList.find(`leaders/player${index + 1}/score`);
            textScore.getComponent(TextComponent).text = entry.score.toString();

            const imageAvatar = topList.find(
                `leaders/player${index + 1}/avatar`
            );
            const resourceId = LeaderboardHelper.getPlayerAvatarId(
                entry.user.playerId,
                entry.user.isCurrentUser
            );
            imageAvatar.getComponent(SpriteComponent).texture = resourceId;
        });
    }

    public static async loadLivesLeaderboard(socialSection: GameObject) {
        LeaderboardHelper.livesMap = {};

        const livesSection = socialSection.find('lives_section');
        const livesContainer = livesSection.find('lives_list');

        for (let i = 1; i <= 10; i++) {
            const friendItem = livesContainer.find(`lives_item_${i}`);

            // Hide and move to parent container - this prevents scroll from accounting for this item's height.
            friendItem.getComponent(Renderable).visible = false;
            socialSection.addChild(friendItem);

            // Reset click listeners.
            friendItem.find('request_button').off(GameObjectEvent.SELECTED);
            friendItem.find('send_button').off(GameObjectEvent.SELECTED);
        }

        // Load friends scores and limit to ten entries.
        // TODO: Cache this with social 'friends' leaderboard since they are the same.
        const leaderboardEntries: IScoreEntry[] = (
            await Game.sdk.getFriendScores(
                Leaderboard.TOTAL_STARS,
                TOTAL_STARS_INSTANCE_ID
            )
        ).slice(0, 10);

        await LeaderboardHelper.getPlayerAvatars(leaderboardEntries);

        leaderboardEntries.forEach((entry, index) => {
            // Don't show current user.
            if (entry.user.playerId === Game.sdk.getUserId()) {
                return;
            }

            // Cache index by player ID for lookup when updating buttons/timers.
            LeaderboardHelper.livesMap[entry.user.playerId] = index + 1;

            // Find in 'socialSection' due to re-parenting mask fix.
            const friendItem = socialSection.find(`lives_item_${index + 1}`);

            // Re-add to section.
            livesContainer.addChild(friendItem);

            // Show entry.
            friendItem.getComponent(Renderable).visible = true;

            // Set entry lives count (random 0-5).
            const lives = Random.int(0, 5);
            friendItem
                .find('lives')
                .getComponent(TextComponent).text = lives.toString();

            // Heart-beat animation
            const heart = friendItem.find('heart');
            if (!heart.hasComponent(AnimationComponent)) {
                heart.addComponent(AnimationComponent);
            }
            StartAnimation(heart, 'Heartbeat', {
                duration: 150 * (lives + 1),
            });

            // Set avatars.
            const imageAvatar = friendItem.find('avatar');
            const resourceId = LeaderboardHelper.getPlayerAvatarId(
                entry.user.playerId,
                entry.user.isCurrentUser
            );
            imageAvatar.getComponent(SpriteComponent).texture = resourceId;

            // Set entry name.
            const textName = friendItem.find('name');
            const textComponent = textName.getComponent(TextComponent);
            textComponent.text = entry.user.handle.slice(0, 8); // Limit to 8 characters.

            // On-click handlers.
            const btnSendLives = friendItem.find('send_button');
            const btnRequestLives = friendItem.find('request_button');

            btnSendLives.on(GameObjectEvent.SELECTED, () => {
                GameActions.setContext(ContextType.SEND_CURRENCY, {
                    friendId: entry.user.playerId,
                });
            });

            btnRequestLives.on(GameObjectEvent.SELECTED, () => {
                Game.sdk.gameTracking(FBTrackingEvent.Coin_Request);
                GameActions.setContext(ContextType.REQUEST_CURRENCY, {
                    friendId: entry.user.playerId,
                });
            });
        });

        // Hacky but w/e... not IE safe.
        window && window.dispatchEvent(new Event('resize'));
    }

    public static async loadSocialLeaderboard(socialSection: GameObject) {
        const friendsData: IFriendData[] = Game.store.getState().playerState
            .social.friendData;
        LeaderboardHelper.friendsMap = {};
        const friendSection = socialSection.find('friends_section');

        // Hide placeholder leader-board entries
        for (let i = 1; i <= 10; i++) {
            /*
            const friendItem: FriendItem = friendSection.find(`friend_item_${i}`) as FriendItem;

            // Hide and move to parent container - this prevents scroll from accounting for this item's height.
            friendItem.getComponent(Renderable).visible = false;
            socialSection.addChild(friendItem);

            // Reset click listener.
            //friendItem.giftButton.off(GameObjectEvent.SELECTED);

            // Show gift button.
            //friendItem.giftButton.visible = true;
            */
        }

        // Load friends scores and limit to ten entries.
        const leaderboardEntries: IScoreEntry[] = (
            await Game.sdk.getFriendScores(
                Leaderboard.TOTAL_STARS,
                TOTAL_STARS_INSTANCE_ID
            )
        ).slice(0, 10);

        await LeaderboardHelper.getPlayerAvatars(leaderboardEntries);

        leaderboardEntries.forEach((entry, index) => {
            /*
            const friendData: IFriendData = friendsData.find(friend => friend.playerId === entry.user.playerId);
            // Find in 'socialSection' due to re-parenting mask fix.
            const friendItem: FriendItem = socialSection.find(`friend_item_${index + 1}`) as FriendItem;

            // Re-add to section.
            friendSection.addChild(friendItem);

            // Show entry.
            friendItem.getComponent(Renderable).visible = true;

            // Set rank.
            friendItem.rank = (index + 1).toString();

            // Set entry name.
            const textName = friendItem.find('name');
            const textComponent = textName.getComponent(TextComponent);
            textComponent.text = entry.user.handle.slice(0, 8); // Limit to 8 characters.

            const rectComponent = friendItem.getComponent(RectComponent);
            if (entry.user.playerId === Game.sdk.getUserId()) {
                textComponent.style.fill = '#FFAD00';
                rectComponent.color = PLAYER_BG_COLOR;

                // Hide gift button for current player.
                friendItem.giftButton
                    .getComponent(Renderable).visible = false;
            } else {
                textComponent.style.fill = '#006EB6';
                rectComponent.color = FRIEND_BG_COLOR;
                friendItem.giftOnCooldown = !(friendData === undefined || friendData.giftCooldown === null);
            }

            // Set entry score.
            const textScore = friendItem.find('score');
            textScore.getComponent(TextComponent).text = entry.score.toString();

            const imageAvatar = friendItem.find('avatar');
            const resourceId = LeaderboardHelper.getPlayerAvatarId(
                entry.user.playerId
            );
            imageAvatar.getComponent(SpriteComponent).texture = resourceId;

            friendItem.updateGiftButtonOnClick(index, entry.user.playerId);

            // friendItem.giftOnCooldown =
            /*
            // On gift click listener:
            friendItem.find('gift_button').on(GameObjectEvent.SELECTED, () => {
            });
            */

            LeaderboardHelper.friendsMap[entry.user.playerId] = index + 1;
        });

        // Hacky but w/e... not IE safe.
        // TODO: Trigger UI resize when children are added to elements.
        window && window.dispatchEvent(new Event('resize'));
    }

    public static async loadWorldLeaderboard(
        worldNumber: number,
        podium: GameObject
    ) {
        // Check cache.
        if (!WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE[worldNumber]) {
            // Pick three random animals.
            const animals = ANIMAL_ENTRY_PLACEHOLDERS.slice(0);
            ArrayHelper.shuffle(animals);
            const animal_1 = animals.pop();
            const animal_2 = animals.pop();
            const animal_3 = animals.pop();

            WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE[worldNumber] = [
                {
                    avatarId: animal_1.avatarId,
                    name: animal_1.name,
                    score: Random.int(125, 150),
                },
                {
                    avatarId: animal_2.avatarId,
                    name: animal_2.name,
                    score: Random.int(100, 120),
                },
                {
                    avatarId: animal_3.avatarId,
                    name: animal_3.name,
                    score: Random.int(85, 99),
                },
            ];
        }

        // Make a copy of the entry cache.
        const newLeaderBoard = WORLD_ANIMAL_LEADERBOARD_ENTRY_CACHE[
            worldNumber
        ].slice(0);

        // Load friends scores and limit to ten entries.
        const leaderboardEntries: IScoreEntry[] = (
            await Game.sdk.getFriendScores(
                Leaderboard.WORLD_STARS,
                worldNumber.toString()
            )
        ).slice(0, 3);

        await LeaderboardHelper.getPlayerAvatars(leaderboardEntries);

        // Unshift leaderboard entries to beginning of cached leaderboard.
        leaderboardEntries.forEach(entry => {
            newLeaderBoard.unshift({
                avatarId: LeaderboardHelper.getPlayerAvatarId(
                    entry.user.playerId,
                    entry.user.isCurrentUser
                ),
                name: entry.user.handle,
                score: entry.score,
            });
        });

        // Real leaderboard entries must be included so splice out remaining (animal) entries.
        newLeaderBoard.splice(3);

        // Then re-sort by score, descending.
        newLeaderBoard.sort((a, b) => b.score - a.score);

        // Apply leaderboard to UI:
        newLeaderBoard.forEach((entry, index) => {
            const color = ['gold', 'silver', 'bronze'][index];

            // Set entry name.
            const textName = podium.find(`podium_${color}/player_name`);
            const textComponent = textName.getComponent(TextComponent);
            textComponent.text = entry.name.slice(0, 8); // Limit to 8 characters.
            // Set entry score.
            const textScore = podium.find(
                `podium_${color}/podium/star_box/star_text`
            );
            textScore.getComponent(TextComponent).text = entry.score.toString();

            const imageAvatar = podium.find(
                `podium_${color}/player_avatar/avatar`
            );
            imageAvatar.getComponent(SpriteComponent).texture = entry.avatarId;
        });
    }

    public static async loadBeatenScore(
        loser: GameObject,
        winner: GameObject,
        levelNumber: number,
        friendId: string
    ) {
        // Load friends scores.
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.LEVEL_SCORE,
            levelNumber.toString()
        );

        // Setup user (winner).
        const userId = Game.sdk.getUserId();
        const userIndex = leaderboardEntries.findIndex(
            e => e.user.playerId === userId
        );
        const userEntry: IScoreEntry = leaderboardEntries[userIndex];
        await LeaderboardHelper.getPlayerAvatar(userEntry);

        const userAvatar = winner.find('avatar').getComponent(SpriteComponent);
        userAvatar.texture = LeaderboardHelper.getPlayerAvatarId(
            userEntry.user.playerId,
            userEntry.user.isCurrentUser
        );
        userAvatar.width = 120;
        userAvatar.height = 120;
        const userName = winner.find('name_text').getComponent(TextComponent);
        userName.text = userEntry.user.handle.slice(0, 8);
        const userScore = winner.find('score_text').getComponent(TextComponent);
        userScore.text = userEntry.score.toString();

        // Setup friend (loser).
        const friendIndex = leaderboardEntries.findIndex(
            e => e.user.playerId === friendId
        );
        const friendEntry: IScoreEntry = leaderboardEntries[friendIndex];
        await LeaderboardHelper.getPlayerAvatar(friendEntry);

        const friendAvatar = loser.find('avatar').getComponent(SpriteComponent);
        friendAvatar.texture = LeaderboardHelper.getPlayerAvatarId(
            friendEntry.user.playerId,
            friendEntry.user.isCurrentUser
        );
        friendAvatar.width = 90;
        friendAvatar.height = 90;
        const friendName = loser.find('name_text').getComponent(TextComponent);
        friendName.text = friendEntry.user.handle.slice(0, 8);
        const friendScore = loser
            .find('score_text')
            .getComponent(TextComponent);
        friendScore.text = friendEntry.score.toString();
    }

    public static async getPlayerAvatars(entries: IScoreEntry[]) {
        for (let i = 0, n = entries.length; i < n; i++) {
            const entry = entries[i];
            // Queue avatars for asynchronous loading.
            const resourceId = this.getPlayerAvatarId(
                entry.user.playerId,
                entry.user.isCurrentUser
            );
            if (!Game.resource.existsOrQueued(resourceId)) {
                if (!entry.user.avatarUrl) {
                    Game.resource.set(
                        resourceId,
                        Game.resource.get('no_avatar')
                    );
                } else {
                    Game.resource.set(
                        resourceId,
                        PIXI.Texture.from(entry.user.avatarUrl)
                    );
                    // TODO: Fix error handling to prevent loading from breaking
                    // Game.resource.add(resourceId, entry.user.avatarUrl);
                }
            }
        }

        await Game.resource.load();
    }

    public static async getPlayerAvatar(entry: IScoreEntry) {
        const resourceId = this.getPlayerAvatarId(
            entry.user.playerId,
            entry.user.isCurrentUser
        );
        if (!Game.resource.existsOrQueued(resourceId)) {
            if (!entry.user.avatarUrl) {
                Game.resource.set(resourceId, 'no_avatar');
            } else {
                Game.resource.add(resourceId, entry.user.avatarUrl);
            }
        }
        await Game.resource.load();

        return resourceId;
    }

    public static getPlayerAvatarId(
        playerId: string,
        isCurrentUser: boolean
    ): string {
        if (isCurrentUser) {
            return Game.player.getAvatarTexture();
        } else {
            return `player_avatar_${playerId}`;
        }
    }

    public static setPlayerAvatar(
        resourceId: string,
        resourcePath: string,
        avatar: UIImageComponent | SpriteComponent,
        size?: number
    ) {
        if (Game.resource.existsAndLoaded(resourceId)) {
            LeaderboardHelper.setAvatar(resourceId, avatar, size);
        } else {
            if (!Game.resource.existsOrQueued(resourceId)) {
                Game.resource.add(
                    resourceId,
                    resourcePath,
                    ResourceDriver.TERTIARY
                );
            }
            Game.resource
                .wait(resourceId)
                .then(() => {
                    LeaderboardHelper.setAvatar(resourceId, avatar, size);
                })
                .catch(() =>
                    LeaderboardHelper.setAvatar('no_avatar', avatar, size)
                );
        }
    }

    private static setAvatar(
        resourceId: string,
        avatar: UIImageComponent | SpriteComponent,
        size?: number
    ): void {
        if (!avatar || !avatar.gameObject || avatar.gameObject.destroyed) {
            return;
        }

        try {
            avatar.texture = resourceId;
        } catch (e) {
            avatar.texture = 'no_avatar';
        }

        if (size) {
            avatar.width = size;
            avatar.height = size;
        }
    }

    public static async loadTotalStarLeaderboard(
        limit?: number
    ): Promise<IScoreEntry[]> {
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_STARS,
            TOTAL_STARS_INSTANCE_ID
        );

        const friendEntries: IScoreEntry[] = limit
            ? leaderboardEntries.slice(0, limit)
            : leaderboardEntries;

        await LeaderboardHelper.getPlayerAvatars(friendEntries);

        return friendEntries;
    }

    /**
     * gets one friend from the total stars leaderboard with no cooldown on helperfriends
     * if there is none, returns null
     */
    public static async getFriendForHelp(): Promise<IScoreEntry> {
        // Load friends scores.
        const leaderboardEntries: IScoreEntry[] = await Game.sdk.getFriendScores(
            Leaderboard.TOTAL_STARS,
            TOTAL_STARS_INSTANCE_ID
        );
        // user IDs to exclude
        const user_blacklist: string[] = [];
        const state = Game.store.getState();
        const contextType: FBContextTypes = FBContextHandler.getContextType();

        if(contextType !== FBContextTypes.Solo) {
            // check context contents for friend ID
            user_blacklist.push(...FBContextHandler.getPlayers());
        }

        return leaderboardEntries.find(entry => {
            if (entry.user.playerId === state.userState.userId || user_blacklist.indexOf(entry.user.playerId) !== -1) {
                return false;
            }

            const friend = state.playerState.social.friendData.find(
                element => element.playerId === entry.user.playerId
            );

            // If the friend does not exist in the state or `helperTimer` is not set then this friend is unused.
            if (!friend || !friend.helperTimer) {
                return true;
            }

            const timeDiff =
                DateHelper.now.getTime() -
                DateHelper.getDate(friend.helperTimer).getTime();

            return timeDiff >= HELPER_FRIENDS_COOLDOWN;
        });
    }
}
